import { CATEGORIES, plural } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";
import { type Location, type LocationKind, send, useChange, useItems, useLocations } from "../api";
import { CategoryChip, ErrorNote, Toolbar, usePageTitle } from "../components/ui";
import { categoryLabel } from "../format";
import { useNotify } from "../notify";
import { useShowLocation } from "./Inventory";

export function LocationsPage() {
  usePageTitle("Locations");
  const notify = useNotify();
  const { data: locations = [], isLoading, error } = useLocations();
  const { data: items = [] } = useItems();
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const showLocation = useShowLocation();
  const add = useChange(
    (n: string, key) => send.location({ name: n, sortOrder: locations.length }, key),
    {
      onSuccess: (l) => {
        setName("");
        notify(`${l.name} added`);
      },
    },
  );
  const patch = useChange(send.patchLocation, { onSuccess: () => setRenaming(null) });
  const stocked = (id: string) =>
    items.filter((i) => i.onHand.byLocation.some((b) => b.locationId === id)).length;

  return (
    <>
      <Toolbar />
      <div className="page-body">
        <h1 className="page-title">Locations</h1>
        <p className="muted" style={{ marginTop: -8 }}>
          Where stock is kept. Every movement names one, so stock can be found and counted by place.
        </p>
        <ErrorNote error={error ?? add.error ?? patch.error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        <div className="list-rows">
          {locations.map((l) => (
            <div key={l.id} className={`location-row ${renaming?.id === l.id ? "" : "opens"}`}>
              {renaming?.id === l.id ? (
                <form
                  className="row"
                  style={{ flex: 1 }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    patch.mutate({ id: l.id, patch: { name: renaming.name } });
                  }}
                >
                  <input
                    className="input"
                    style={{ flex: 1 }}
                    value={renaming.name}
                    aria-label="Location name"
                    onChange={(e) => setRenaming({ id: l.id, name: e.target.value })}
                    // biome-ignore lint/a11y/noAutofocus: renaming in place
                    autoFocus
                  />
                  <button type="submit" className="button small primary">
                    Save
                  </button>
                  <button type="button" className="button small" onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  {/* The whole row opens Inventory on what is in this location. */}
                  <Link
                    to="/"
                    className={`location-link ${l.archived ? "faint" : ""}`}
                    onClick={() => showLocation(l.id)}
                  >
                    {l.name}
                  </Link>
                  {l.archived ? <span className="chip plain">Archived</span> : null}
                  <span className="muted">
                    {stocked(l.id)} {plural("item", String(stocked(l.id)))} here
                  </span>
                  <DefaultChips kinds={l.defaultFor} />
                  <span className="spacer" />
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setRenaming({ id: l.id, name: l.name })}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => patch.mutate({ id: l.id, patch: { archived: !l.archived } })}
                  >
                    {l.archived ? "Restore" : "Archive"}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        <form
          className="row"
          style={{ marginTop: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) add.mutate(name.trim());
          }}
        >
          <input
            className="input"
            style={{ maxWidth: 320 }}
            placeholder="New location, e.g. Hop freezer"
            aria-label="New location"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="submit" className="button primary" disabled={!name.trim() || add.isPending}>
            <Plus size={16} /> Add
          </button>
        </form>
        <DeliveryDefaults locations={locations} />
      </div>
    </>
  );
}
/** The kinds of stock a location is the default for, in their colors. */
function DefaultChips({ kinds }: { kinds: readonly LocationKind[] }) {
  if (kinds.length === 0) return null;
  const ordered = [...kinds].sort((a, b) => kindOrder(a) - kindOrder(b));
  return (
    <span className="default-chips" title="Deliveries of these go here unless told otherwise">
      {ordered.map((k) =>
        k === "*" ? (
          <span key={k} className="chip everything-else">
            Everything else
          </span>
        ) : (
          <CategoryChip key={k} category={k} />
        ),
      )}
    </span>
  );
}
const kindOrder = (kind: string) =>
  kind === "*" ? -1 : (CATEGORIES as readonly string[]).indexOf(kind);
/**
 * Where a delivery sends each kind of stock unless told otherwise (Tyler, 2026-10-01): grain to the
 * grain room, hops to the hop freezer, everything else to dry storage. The invoice's last step starts
 * each kind there, and it can be changed on the invoice. A kind has one location, so choosing one
 * moves it from wherever it was.
 */
export function DeliveryDefaults({ locations }: { locations: Location[] }) {
  const open = locations.filter((l) => !l.archived);
  const ownerOf = (kind: LocationKind) => open.find((l) => l.defaultFor.includes(kind));
  const change = useChange(send.patchLocation);
  const setKind = (kind: LocationKind, locationId: string) => {
    if (locationId) {
      const to = open.find((l) => l.id === locationId) as Location;
      change.mutate({
        id: to.id,
        patch: { defaultFor: [...to.defaultFor.filter((k) => k !== kind), kind] },
      });
    } else {
      const from = ownerOf(kind);
      if (from) {
        change.mutate({
          id: from.id,
          patch: { defaultFor: from.defaultFor.filter((k) => k !== kind) },
        });
      }
    }
  };
  const kinds: LocationKind[] = ["*", ...CATEGORIES];

  return (
    <section id="where-deliveries-go" className="section" style={{ marginTop: 24 }}>
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Where deliveries go</h2>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ margin: 0 }}>
          When an invoice is received, each kind of stock on it starts in its location here. It can
          still be changed on the invoice.
        </p>
        <ErrorNote error={change.error} />
        <div className="kind-defaults">
          {kinds.map((kind) => {
            const label = kind === "*" ? "Everything else" : null;
            return (
              <div className="kind-default" key={kind}>
                {label ? <strong>{label}</strong> : <CategoryChip category={kind} />}
                <select
                  className="select"
                  aria-label={`Where ${label ?? categoryLabel(kind)} goes`}
                  value={ownerOf(kind)?.id ?? ""}
                  disabled={change.isPending}
                  onChange={(e) => setKind(kind, e.target.value)}
                >
                  <option value="">
                    {kind === "*" ? "Choose on each invoice" : "With everything else"}
                  </option>
                  {open.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
